<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import KavenegarTemplateParser from 'dashboard/components-next/kavenegar/KavenegarTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t: translate } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getSMSInboxes'),
  getFilteredKavenegarTemplates: useMapGetter(
    'inboxes/getFilteredKavenegarTemplates'
  ),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  templateId: null,
  useTemplate: false,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const rules = computed(() => {
  const baseRules = {
    title: { required, minLength: minLength(1) },
    inboxId: { required },
    scheduledAt: { required },
    selectedAudience: { required },
  };

  if (state.useTemplate) {
    return {
      ...baseRules,
      templateId: { required },
    };
  }

  return {
    ...baseRules,
    message: { required, minLength: minLength(1) },
  };
});

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const selectedInbox = computed(() =>
  formState.inboxes.value?.find(
    inbox => Number(inbox.id) === Number(state.inboxId)
  )
);

const isKavenegarInbox = computed(
  () => selectedInbox.value?.provider === 'kavenegar'
);

const templateOptions = computed(() => {
  if (!state.inboxId || !isKavenegarInbox.value) return [];
  const templates = formState.getFilteredKavenegarTemplates.value(
    state.inboxId
  );
  return templates.map(template => ({
    value: template.id,
    label: template.name,
    template: template,
  }));
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.SMS.CREATE.FORM';
  if (!v$.value[field]) return '';
  return v$.value[field].$error
    ? translate(`${baseKey}.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const details = {
    title: state.title,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };

  if (state.useTemplate && selectedTemplate.value) {
    details.message = templateParserRef.value?.renderedTemplate || '';
    details.template_params = {
      name: selectedTemplate.value.name,
      tokens: templateParserRef.value?.processedParams || {},
    };
  } else {
    details.message = state.message;
  }

  return details;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.useTemplate = false;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="translate('CAMPAIGN.SMS.CREATE.FORM.TITLE.LABEL')"
      :placeholder="translate('CAMPAIGN.SMS.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ translate('CAMPAIGN.SMS.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="translate('CAMPAIGN.SMS.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div v-if="isKavenegarInbox" class="flex items-center gap-2 py-2">
      <input
        id="use-kave-template"
        v-model="state.useTemplate"
        type="checkbox"
        class="w-4 h-4 rounded border-n-weak text-n-blue-9 cursor-pointer"
      />
      <div
        class="flex flex-col cursor-pointer"
        @click="state.useTemplate = !state.useTemplate"
      >
        <label
          for="use-kave-template"
          class="text-sm font-medium text-n-slate-12 cursor-pointer"
        >
          {{ translate('CAMPAIGN.SMS.CREATE.FORM.USE_TEMPLATE.LABEL') }}
        </label>
        <p class="text-xs text-n-slate-11">
          {{ translate('CAMPAIGN.SMS.CREATE.FORM.USE_TEMPLATE.INFO') }}
        </p>
      </div>
    </div>

    <!-- Template Mode -->
    <div
      v-if="isKavenegarInbox && state.useTemplate"
      class="flex flex-col gap-4"
    >
      <div v-if="templateOptions.length" class="flex flex-col gap-1">
        <label
          for="template"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ translate('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.LABEL') }}
        </label>
        <ComboBox
          id="template"
          v-model="state.templateId"
          :options="templateOptions"
          :has-error="!!formErrors.template"
          :placeholder="
            translate('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.PLACEHOLDER')
          "
          :message="formErrors.template"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />

        <!-- Kavenegar Template Parser -->
        <KavenegarTemplateParser
          v-if="selectedTemplate"
          ref="templateParserRef"
          :template="selectedTemplate"
          class="mt-4"
        />
      </div>
      <div
        v-else
        class="p-4 rounded-xl border border-n-amber-3 bg-n-amber-1 text-n-amber-11 text-sm italic"
      >
        {{ translate('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.NO_TEMPLATES') }}
      </div>
    </div>

    <!-- Standard Message Mode -->
    <TextArea
      v-if="!(isKavenegarInbox && state.useTemplate)"
      v-model="state.message"
      :label="translate('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="translate('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ translate('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="translate('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="
          translate('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.PLACEHOLDER')
        "
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="translate('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="
        translate('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
      "
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div
      class="flex flex-col-reverse sm:flex-row items-center justify-between w-full gap-3"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="translate('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full sm:w-auto sm:min-w-[8rem] bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="translate('CAMPAIGN.SMS.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full sm:w-auto sm:min-w-[8rem]"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
